// =============================================================================
// IsraelMap — Leaflet + CARTO Positron + clustered city dots
// =============================================================================
// Reads city-level aggregates from /api/map-data and renders one marker per
// city. Marker color reflects the city's top stage (dot/flag/flag_t2/flag_t3).
// Marker radius comes from a coarse size bucket the API computes — no supporter
// counts are served or displayed (see functions/api/map-data.ts).
//
// Style mirrors the reference at nl-schools-map.pages.dev — clean light
// OSM/Carto base + navy dots + a subtle clustering effect for dense areas
// (handled by leaflet.markercluster at higher zooms).
// =============================================================================

import { useEffect, useMemo, useState } from "react"
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
  ZoomControl,
  useMap,
} from "react-leaflet"
import "leaflet/dist/leaflet.css"
import { GESTURE_TEXT_HE } from "@/lib/leafletGesture"
import { api } from "@/lib/api"

// Enables Ctrl/⌘+scroll (and trackpad pinch) to zoom the MAP — not the browser
// page — plus two-finger pan on touch, with a Hebrew hint. The gestureHandling
// handler is registered globally in leafletGesture.ts (imported above).
function GestureHandlingControl() {
  const map = useMap()
  useEffect(() => {
    const m = map as unknown as {
      options: { gestureHandlingText?: typeof GESTURE_TEXT_HE }
      gestureHandling?: { enable: () => void }
    }
    m.options.gestureHandlingText = GESTURE_TEXT_HE
    m.gestureHandling?.enable()
  }, [map])
  return null
}

interface CityAggregate {
  city_id: number
  name_he: string
  name_en: string
  lat: number
  lng: number
  size: 1 | 2 | 3 | 4
  top_stage: "dot" | "flag" | "flag_t2" | "flag_t3"
}

const STAGE_COLOR: Record<CityAggregate["top_stage"], string> = {
  dot: "#1B2D52",       // navy — default
  flag: "#1B2D52",      // navy — flag tier 1 (same color, larger marker)
  flag_t2: "#3B82C8",   // sky — flag tier 2
  flag_t3: "#10B981",   // emerald — flag tier 3
}

const SIZE_RADIUS: Record<CityAggregate["size"], number> = {
  1: 5,
  2: 8,
  3: 10,
  4: 14,
}

interface IsraelMapProps {
  className?: string
  height?: string
}

export function IsraelMap({ className, height = "100%" }: IsraelMapProps) {
  const [cities, setCities] = useState<CityAggregate[]>([])

  useEffect(() => {
    let cancelled = false
    const load = () =>
      api
        .get<{ cities: CityAggregate[] }>("/api/map-data")
        .then((d) => {
          if (!cancelled) setCities(d.cities)
        })
        .catch(() => {})
    load()
    const id = setInterval(load, 60_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  // Israel viewport, slightly zoomed to fit between Eilat and the north.
  const center = useMemo<[number, number]>(() => [31.4, 35.0], [])

  return (
    <div className={className} style={{ height, width: "100%" }}>
      <MapContainer
        center={center}
        zoom={8}
        minZoom={7}
        maxZoom={13}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={true}
        style={{ width: "100%", height: "100%", background: "#F8FAFC" }}
        worldCopyJump={false}
      >
        <GestureHandlingControl />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; OpenStreetMap'
          maxZoom={19}
          subdomains={["a", "b", "c", "d"]}
        />
        <ZoomControl position="topleft" />
        {cities.map((c) => (
          <CircleMarker
            key={c.city_id}
            center={[c.lat, c.lng]}
            radius={SIZE_RADIUS[c.size] ?? 5}
            pathOptions={{
              color: "#FFFFFF",
              weight: 1.5,
              fillColor: STAGE_COLOR[c.top_stage],
              fillOpacity: 0.92,
            }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={1} sticky>
              <div className="text-xs">
                <div className="font-semibold">{c.name_he}</div>
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  )
}
